import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, TrendingUp, DollarSign, Activity, Building, Users, MapPin, Award, Star, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { isAuthenticated, getUserRole } from '../utils/auth';
import ConsultationRequestDialog from '../components/ConsultationRequestDialog';

export default function TreatmentDetail() {
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTreatmentDetails();
  }, [treatmentId]);

  const fetchTreatmentDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/treatments/${treatmentId}`);
      
      if (!response.ok) {
        throw new Error('Treatment not found');
      }
      
      const data = await response.json();
      setTreatment(data.treatment);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getHospitalSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleRequestConsultation = () => {
    setConsultOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="loading-state">
          <div className="text-6xl mb-4">💊</div>
          <p className="text-lg text-gray-600">Loading treatment details...</p>
        </div>
      </div>
    );
  }

  if (error || !treatment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="error-state">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Treatment Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The treatment you\'re looking for doesn\'t exist.'}</p>
          <Button onClick={() => navigate('/treatments')} data-testid="back-to-treatments-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Treatments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="treatment-detail-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🩹</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                MedRahi
              </h1>
              <p className="text-xs text-gray-600">Healthcare Without Borders</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/treatments')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Treatments
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50" data-testid="hero-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Activity className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <Badge className="mb-3 bg-orange-100 text-orange-800" data-testid="treatment-category">
                    {treatment.category}
                  </Badge>
                  <h1 className="font-heading text-4xl font-bold text-gray-900 mb-4" data-testid="treatment-name">
                    {treatment.name}
                  </h1>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4" data-testid="treatment-description">
                    {treatment.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Badge variant="outline" className="px-3 py-1">
                      {treatment.procedure_type}
                    </Badge>
                    {treatment.is_surgical && (
                      <Badge variant="outline" className="px-3 py-1 border-orange-300 text-orange-700">
                        Surgical Procedure
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-orange-200 shadow-lg" data-testid="quick-stats-card">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Treatment Info</h3>
                  
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <Clock className="w-5 h-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Duration</p>
                      <p className="font-semibold text-gray-900" data-testid="treatment-duration">{treatment.duration}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <Activity className="w-5 h-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Recovery Time</p>
                      <p className="font-semibold text-gray-900" data-testid="recovery-time">{treatment.recovery_time}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Success Rate</p>
                      <p className="font-semibold text-green-600" data-testid="success-rate">{treatment.success_rate}%</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 py-2">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Estimated Cost</p>
                      <p className="font-semibold text-gray-900" data-testid="cost-range">{treatment.cost_range}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Procedures within this specialty */}
      {treatment.procedure_list && treatment.procedure_list.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="procedures-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3">
              Procedures under {treatment.name}
            </h2>
            <p className="text-gray-600 mb-8">
              Explore each procedure in detail — costs, recovery, what to expect and FAQs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {treatment.procedure_list.map((proc, idx) => (
                <Card
                  key={idx}
                  className="group hover:shadow-xl transition-all cursor-pointer border-2 hover:border-orange-300"
                  onClick={() => navigate(`/procedures/${proc.slug}`)}
                  data-testid={`procedure-card-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      {proc.success_rate && (
                        <Badge className="bg-green-100 text-green-700 border-0">{proc.success_rate}% success</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-3 group-hover:text-orange-600 transition-colors" data-testid={`procedure-name-${idx}`}>
                      {proc.name}
                    </h3>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Cost</span>
                        <span className="font-semibold text-gray-900">{proc.cost_range}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 flex items-center gap-1"><Clock className="w-4 h-4" /> Recovery</span>
                        <span className="font-medium text-gray-700">{proc.recovery_time}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-orange-600 font-medium text-sm group-hover:gap-2 gap-1 transition-all">
                      View procedure details <ArrowRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hospitals Offering Treatment */}
      {treatment.hospitals && treatment.hospitals.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="hospitals-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
              Hospitals Offering {treatment.name}
            </h2>
            <p className="text-gray-600 mb-8">
              {treatment.hospitals.length} accredited hospitals providing this treatment across India
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {treatment.hospitals.map((hospital, idx) => (
                <Card 
                  key={idx} 
                  className="hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => navigate(`/hospitals/${getHospitalSlug(hospital.name)}`)}
                  data-testid={`hospital-card-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{hospital.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                          <MapPin className="w-3 h-3" />
                          <span>{hospital.city}, {hospital.state}</span>
                        </div>
                        <Badge className="text-xs bg-orange-100 text-orange-800">
                          <Award className="w-3 h-3 mr-1" />
                          {hospital.accreditation}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Beds</span>
                        <span className="font-semibold">{hospital.beds}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Medical Staff</span>
                        <span className="font-semibold">{hospital.doctors}</span>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/hospitals/${getHospitalSlug(hospital.name)}`);
                      }}
                    >
                      View Hospital Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Qualified Doctors */}
      {treatment.doctors && treatment.doctors.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="doctors-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
              Qualified Specialists
            </h2>
            <p className="text-gray-600 mb-8">
              Expert doctors specializing in {treatment.specialty}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {treatment.doctors.slice(0, 9).map((doctor, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow" data-testid={`doctor-card-${idx}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{doctor.full_name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{doctor.specialization}</p>
                        {doctor.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-semibold">{doctor.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Experience</span>
                        <span className="font-semibold">{doctor.experience_years} years</span>
                      </div>
                      {doctor.consultation_fee > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Consultation</span>
                          <span className="font-semibold text-green-600">${doctor.consultation_fee}</span>
                        </div>
                      )}
                      {doctor.hospital_affiliations && doctor.hospital_affiliations.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">Affiliated with:</p>
                          <p className="text-xs text-gray-700 font-medium">{doctor.hospital_affiliations[0]}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Treatment Process */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="process-section">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-12 text-center">
            Treatment Journey
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-2 border-orange-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Consultation</h3>
                <p className="text-sm text-gray-600">Initial assessment and diagnosis with qualified specialists</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Plan</h3>
                <p className="text-sm text-gray-600">Personalized plan with cost estimate and timeline</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Procedure</h3>
                <p className="text-sm text-gray-600">Treatment performed by experienced medical team</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">4</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Recovery</h3>
                <p className="text-sm text-gray-600">Post-treatment care and follow-up support</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="cta-section">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Ready to Begin Your Treatment?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Connect with top specialists and accredited hospitals for {treatment.name}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={handleRequestConsultation}
              data-testid="request-consultation-btn"
            >
              Request Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/treatments')}
              data-testid="explore-treatments-btn"
            >
              Explore More Treatments
            </Button>
          </div>
        </div>
      </section>
      <ConsultationRequestDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        context={{ type: 'treatment', name: treatment.name, treatmentCode: treatment.name }}
      />
    </div>
  );
}
